import { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  Grid, Church, GraduationCap, Briefcase, Heart, Calendar, User, Minus, Check, Sparkles 
} from 'lucide-react';
import { BRANDING_TEMPLATES, TEMPLATE_CATEGORIES } from '@/data/brandingTemplates';

const CATEGORY_ICONS = {
  Grid, Church, GraduationCap, Briefcase, Heart, Calendar, User, Minus
};

export default function BrandingTemplateGallery({ open, onClose, onApply, currentLogo }) {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [hoveredTemplate, setHoveredTemplate] = useState(null);

  const filteredTemplates = selectedCategory === 'all' 
    ? BRANDING_TEMPLATES 
    : BRANDING_TEMPLATES.filter(t => t.category === selectedCategory);

  const handleApply = () => {
    if (selectedTemplate) {
      onApply({
        ...selectedTemplate.settings,
        branding_enabled: selectedTemplate.id !== 'no-branding',
        branding_text: selectedTemplate.sample_text || ''
      });
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] p-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-500" />
            Branding Templates
          </DialogTitle>
          <DialogDescription>
            Choose a preset branding style to apply instantly
          </DialogDescription>
        </DialogHeader>

        <div className="flex h-[500px]">
          {/* Category Sidebar */}
          <div className="w-48 border-r bg-slate-50 dark:bg-slate-900 p-3 flex-shrink-0">
            <ScrollArea className="h-full">
              <div className="space-y-1">
                {TEMPLATE_CATEGORIES.map((category) => {
                  const Icon = CATEGORY_ICONS[category.icon] || Grid;
                  return (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                        selectedCategory === category.id
                          ? 'bg-amber-500 text-black font-medium'
                          : 'hover:bg-slate-200 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {category.name}
                    </button>
                  );
                })}
              </div>
            </ScrollArea>
          </div>

          {/* Templates Grid */}
          <ScrollArea className="flex-1 p-4">
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredTemplates.map((template) => (
                <motion.div
                  key={template.id}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setSelectedTemplate(template)}
                  onMouseEnter={() => setHoveredTemplate(template.id)}
                  onMouseLeave={() => setHoveredTemplate(null)}
                  className={`relative cursor-pointer rounded-xl overflow-hidden border-2 transition-all ${
                    selectedTemplate?.id === template.id
                      ? 'border-amber-500 ring-2 ring-amber-500/30'
                      : 'border-transparent hover:border-slate-300 dark:hover:border-slate-600'
                  }`}
                  data-testid={`template-${template.id}`}
                >
                  {/* Preview */}
                  <div className={`aspect-video bg-gradient-to-br ${template.preview_bg} relative`}>
                    {/* Simulated slide content */}
                    <div className="absolute inset-0 flex items-center justify-center p-4">
                      <div className="text-center">
                        <div className="w-12 h-1 bg-white/30 rounded mx-auto mb-2" />
                        <div className="w-20 h-1 bg-white/20 rounded mx-auto" />
                      </div>
                    </div>

                    {/* Logo placeholder */}
                    {template.settings.branding_logo_position && template.id !== 'no-branding' && (
                      <div 
                        className={`absolute ${
                          template.settings.branding_logo_position === 'top-left' ? 'top-2 left-2' :
                          template.settings.branding_logo_position === 'top-right' ? 'top-2 right-2' :
                          template.settings.branding_logo_position === 'bottom-left' ? 'bottom-6 left-2' :
                          'bottom-6 right-2'
                        }`}
                        style={{ opacity: template.settings.branding_logo_opacity }}
                      >
                        {currentLogo ? (
                          <img 
                            src={currentLogo} 
                            alt="" 
                            className={`object-contain ${
                              template.settings.branding_logo_size === 'small' ? 'h-4' :
                              template.settings.branding_logo_size === 'medium' ? 'h-6' :
                              'h-8'
                            }`}
                          />
                        ) : (
                          <div className={`bg-white/30 rounded ${
                            template.settings.branding_logo_size === 'small' ? 'w-8 h-4' :
                            template.settings.branding_logo_size === 'medium' ? 'w-12 h-6' :
                            'w-16 h-8'
                          }`} />
                        )}
                      </div>
                    )}

                    {/* Branding text placeholder */}
                    {template.sample_text && (
                      <div 
                        className={`absolute text-[8px] text-white/60 ${
                          template.settings.branding_text_position === 'bottom-left' ? 'bottom-6 left-2' :
                          template.settings.branding_text_position === 'bottom-right' ? 'bottom-6 right-2' :
                          'bottom-6 left-1/2 transform -translate-x-1/2'
                        }`}
                        style={{ opacity: template.settings.branding_logo_opacity }}
                      >
                        {template.sample_text}
                      </div>
                    )}

                    {/* Simulated control bar */}
                    <div className="absolute bottom-0 left-0 right-0 h-4 bg-black/40" />

                    {/* Selected indicator */}
                    {selectedTemplate?.id === template.id && (
                      <div className="absolute top-2 right-2 w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center">
                        <Check className="w-4 h-4 text-black" />
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="p-3 bg-white dark:bg-slate-800">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-medium text-sm">{template.name}</h4>
                        <p className="text-xs text-muted-foreground line-clamp-1">{template.description}</p>
                      </div>
                    </div>
                  </div>

                  {/* Hover overlay with details */}
                  {hoveredTemplate === template.id && (
                    <motion.div 
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="absolute inset-0 bg-black/80 flex items-center justify-center p-4"
                    >
                      <div className="text-center text-white">
                        <p className="text-sm font-medium mb-2">{template.name}</p>
                        <div className="text-xs space-y-1 text-white/70">
                          <p>Position: {template.settings.branding_logo_position}</p>
                          <p>Size: {template.settings.branding_logo_size}</p>
                          <p>Opacity: {Math.round(template.settings.branding_logo_opacity * 100)}%</p>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              ))}
            </div>
          </ScrollArea>
        </div>

        {/* Selected Template Preview */}
        {selectedTemplate && (
          <div className="px-6 py-3 bg-amber-50 dark:bg-amber-900/20 border-t border-amber-200 dark:border-amber-800">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Badge className="bg-amber-500 text-black">{selectedTemplate.name}</Badge>
                <span className="text-sm text-amber-700 dark:text-amber-300">
                  {selectedTemplate.description}
                </span>
              </div>
              <div className="text-xs text-amber-600 dark:text-amber-400">
                Logo: {selectedTemplate.settings.branding_logo_position} • 
                Size: {selectedTemplate.settings.branding_logo_size} • 
                Opacity: {Math.round(selectedTemplate.settings.branding_logo_opacity * 100)}%
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="px-6 pb-6 pt-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleApply} 
            disabled={!selectedTemplate}
            className="bg-amber-500 hover:bg-amber-600 text-black"
          >
            <Check className="w-4 h-4 mr-2" />
            Apply Template
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
